import type { VisitorApproverMemberType } from "src/api/product/product-types";

/**
 * 방문자 셀프 등록 승인 방식
 * - AUTO: 자동승인 (승인자 설정 불필요)
 * - MANUAL: 수동승인 (승인자 설정 필요)
 * - "": 미선택 (셀프 등록 미사용이거나 아직 선택 전)
 */
export type VisitorApprovalMode = "" | "AUTO" | "MANUAL";

/**
 * 방문자 셀프 등록 승인 이용자 유형 체크박스 상태
 * - isInviter: "이용자" 체크 여부
 * - isAdmin: "이용자 마스터" 체크 여부
 */
export type VisitorApproverChecks = {
  isInviter: boolean;
  isAdmin: boolean;
};

/**
 * 승인 이용자 유형(enum) → 승인 방식으로 변환
 * - MEMBER_AUTO: 자동승인
 * - 그 외 유형 값: 수동승인
 * - 빈 값: 미선택
 */
export const visitorApproverTypeToMode = (type?: VisitorApproverMemberType | "" | null): VisitorApprovalMode => {
  if (type === "MEMBER_AUTO") return "AUTO";
  return type ? "MANUAL" : "";
};

/**
 * 승인 이용자 유형(enum) → 체크박스 상태로 변환
 * - MEMBER_INVITER: 이용자
 * - MEMBER_ADMIN: 이용자 마스터
 * - MEMBER_APPROVER: 이용자 + 이용자 마스터 (동시)
 * - 그 외(MEMBER_AUTO, 빈 값): 미선택
 */
export const visitorApproverTypeToChecks = (type?: VisitorApproverMemberType | "" | null): VisitorApproverChecks => {
  switch (type) {
    case "MEMBER_INVITER":
      return { isInviter: true, isAdmin: false };
    case "MEMBER_ADMIN":
      return { isInviter: false, isAdmin: true };
    case "MEMBER_APPROVER":
      return { isInviter: true, isAdmin: true };
    default:
      return { isInviter: false, isAdmin: false };
  }
};

/**
 * 체크박스 상태 → 승인 이용자 유형(enum)으로 변환
 * - 이용자 + 이용자 마스터: MEMBER_APPROVER
 * - 이용자만: MEMBER_INVITER
 * - 이용자 마스터만: MEMBER_ADMIN
 * - 미선택: "" (빈 값)
 */
export const visitorApproverChecksToType = ({ isInviter, isAdmin }: VisitorApproverChecks): VisitorApproverMemberType | "" => {
  if (isInviter && isAdmin) return "MEMBER_APPROVER";
  if (isInviter) return "MEMBER_INVITER";
  if (isAdmin) return "MEMBER_ADMIN";
  return "";
};

/**
 * 방문자 초대 승인의 승인권자 라벨
 * 초대 행위 자체가 이용자의 의사표시이므로 셀프 등록과 달리 승인권자는 이용자 마스터로 고정된다.
 */
export const VISITOR_INVITE_APPROVER_LABEL = "이용자 마스터";

/**
 * 방문자 관리 하위 설정(회색 박스)의 선택지 텍스트 영역 폭 고정 클래스
 * 디자인 가이드: 국문 80 / 영문 160 (현재 화면 라벨은 모두 국문 고정)
 * 인풋이나 추가 옵션이 붙는 선택지(제한, 수동승인)는 예외로 폭을 고정하지 않는다.
 */
export const VISITOR_OPTION_LABEL_CLASS = "minmax80";

/**
 * 읽기 모드에서 표시할 승인 방식 라벨
 * 예) MEMBER_AUTO → "자동승인", MEMBER_APPROVER → "수동승인"
 */
export const visitorApprovalModeLabel = (type?: VisitorApproverMemberType | "" | null): string => {
  switch (visitorApproverTypeToMode(type)) {
    case "AUTO":
      return "자동승인";
    case "MANUAL":
      return "수동승인";
    default:
      return "-";
  }
};

/**
 * 읽기 모드에서 표시할 승인 이용자 유형 라벨
 * 예) MEMBER_APPROVER → "이용자, 이용자 마스터"
 * 자동승인(MEMBER_AUTO)은 승인자 설정이 없으므로 "-"
 */
export const visitorApproverTypeLabel = (type?: VisitorApproverMemberType | "" | null): string => {
  const { isInviter, isAdmin } = visitorApproverTypeToChecks(type);
  const labels: string[] = [];
  if (isInviter) labels.push("이용자");
  if (isAdmin) labels.push("이용자 마스터");
  return labels.length ? labels.join(", ") : "-";
};

/**
 * 읽기 모드 '승인방식' 한 줄 요약 라벨
 * 승인자 설정이 승인방식 행에 함께 노출되므로 수동승인일 때만 괄호로 승인자를 덧붙인다.
 * 예) MEMBER_AUTO → "자동승인", MEMBER_APPROVER → "수동승인 (이용자, 이용자 마스터)"
 */
export const visitorApprovalSummaryLabel = (type?: VisitorApproverMemberType | "" | null): string => {
  if (visitorApproverTypeToMode(type) !== "MANUAL") return visitorApprovalModeLabel(type);
  return `수동승인 (${visitorApproverTypeLabel(type)})`;
};
